// Email Connection Step for Onboarding
// Integrates Gmail OAuth, database storage, and N8N processing

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import GmailOAuth from '@/components/auth/GmailOAuth';
import { useEmailProcessing } from '@/hooks/email/useEmailProcessing';
import { useEmailConnection } from '@/hooks/email/useEmailConnection';
import { 
  Mail, 
  CheckCircle, 
  XCircle, 
  Loader2, 
  AlertTriangle,
  ArrowRight,
  ArrowLeft,
  RefreshCw,
  Sparkles
} from 'lucide-react';

export default function EmailConnectionStep({ 
  user, 
  onComplete, 
  onSkip, 
  onBack,
  isActive = false 
}) {
  const [step, setStep] = useState('intro'); // intro, connecting, processing, success, error
  const [processingResult, setProcessingResult] = useState(null);
  const [error, setError] = useState(null);
  const [isRetrying, setIsRetrying] = useState(false);

  // Hooks for email functionality
  const {
    isConnected: isEmailConnected,
    syncStatus: emailSyncStatus,
    error: emailError
  } = useEmailConnection();

  const {
    isProcessing,
    lastResult,
    error: processingError,
    processHistorical,
    retryProcessing,
    clearError
  } = useEmailProcessing();

  // Reset state when component becomes active
  useEffect(() => {
    if (isActive) {
      setStep('intro');
      setProcessingResult(null);
      setError(null);
      clearError();
    }
  }, [isActive, clearError]);

  // Handle Gmail OAuth success
  const handleOAuthSuccess = async (oauthData) => {
    console.log('Gmail OAuth success:', oauthData);
    setStep('connecting');
    setError(null);

    try {
      // Step 1: Save email connection (Step 3 service)
      // This is handled by the GmailOAuth component internally
      // We just need to wait for the connection to be saved
      
      // Wait a moment for the connection to be saved
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Step 2: Trigger historical email processing (Step 4 service)
      setStep('processing');
      const result = await processHistorical(user.id);
      
      if (result.success) {
        setProcessingResult(result);
        setStep('success');
        onComplete?.();
      } else {
        setError(result.message || 'Email processing failed');
        setStep('error');
      }
    } catch (err) {
      console.error('Error in OAuth success handler:', err);
      setError(err.message || 'Failed to process emails');
      setStep('error');
    }
  };

  // Handle Gmail OAuth error
  const handleOAuthError = (errorMessage) => {
    console.error('Gmail OAuth error:', errorMessage);
    setError(errorMessage);
    setStep('error');
  };

  // Handle retry processing
  const handleRetryProcessing = async () => {
    setIsRetrying(true);
    setError(null);
    setStep('processing');

    try {
      const result = await retryProcessing(user.id, 'historical');
      
      if (result.success) {
        setProcessingResult(result);
        setStep('success');
        onComplete?.();
      } else {
        setError(result.message || 'Retry failed');
        setStep('error');
      }
    } catch (err) {
      console.error('Error retrying processing:', err);
      setError(err.message || 'Retry failed');
      setStep('error');
    } finally {
      setIsRetrying(false);
    }
  };

  // Handle skip
  const handleSkip = () => {
    onSkip?.();
  };

  // Handle back
  const handleBack = () => {
    onBack?.();
  };

  // Render different steps
  const renderIntro = () => (
    <div className="text-center space-y-6">
      <div className="mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
        <Mail className="w-8 h-8 text-blue-600" />
      </div>
      
      <div>
        <h3 className="text-xl font-semibold text-gray-900 mb-2">
          Connect Your Gmail Account
        </h3>
        <p className="text-gray-600 leading-relaxed">
          We'll scan your emails for camp registrations, confirmations, and reminders to help you keep track of all your kids' activities.
        </p>
      </div>

      <div className="bg-blue-50 rounded-lg p-4 text-left space-y-2">
        <h4 className="font-medium text-blue-900">What we'll find:</h4>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• Camp registration confirmations</li>
          <li>• Activity reminders and updates</li>
          <li>• Payment receipts and invoices</li>
          <li>• Schedule changes and notifications</li>
        </ul>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        {/* TEMPORARILY DISABLED IN PRODUCTION - Gmail OAuth component handles the disabled state */}
        <GmailOAuth
          onSuccess={handleOAuthSuccess}
          onError={handleOAuthError}
          buttonText="Connect Gmail Account"
          className="flex-1"
        />
        <Button
          variant="outline"
          onClick={handleSkip}
          className="flex-1"
        >
          Skip for Now
        </Button>
      </div>

      <p className="text-xs text-gray-500">
        You can connect your email later in Settings
      </p>
    </div>
  );

  const renderConnecting = () => (
    <div className="text-center space-y-6">
      <div className="mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
      </div>
      
      <div>
        <h3 className="text-xl font-semibold text-gray-900 mb-2">
          Saving Your Email Connection
        </h3>
        <p className="text-gray-600">
          We're securely storing your Gmail connection...
        </p>
      </div>
    </div>
  );

  const renderProcessing = () => (
    <div className="text-center space-y-6">
      <div className="mx-auto w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-brand-600 animate-spin" />
      </div>
      
      <div>
        <h3 className="text-xl font-semibold text-gray-900 mb-2">
          Scanning Your Emails
        </h3>
        <p className="text-gray-600">
          We're looking through your emails for camp information. This may take 30-60 seconds...
        </p>
      </div>

      <div className="bg-purple-50 rounded-lg p-4">
        <div className="flex items-center justify-center space-x-2">
          <Sparkles className="w-4 h-4 text-brand-600" />
          <span className="text-sm text-brand-800">
            Processing in progress...
          </span>
        </div>
      </div>
    </div>
  );

  const renderSuccess = () => (
    <div className="text-center space-y-6">
      <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
        <CheckCircle className="w-8 h-8 text-green-600" />
      </div>
      
      <div>
        <h3 className="text-xl font-semibold text-gray-900 mb-2">
          Email Connected Successfully!
        </h3>
        <p className="text-gray-600">
          We found camp information in your emails.
        </p>
      </div>

      {processingResult && (
        <div className="bg-green-50 rounded-lg p-4 space-y-2">
          <div className="flex items-center justify-center space-x-2">
            <Badge variant="secondary" className="bg-green-100 text-green-800">
              {processingResult.emails_processed || 0} emails processed
            </Badge>
            <Badge variant="secondary" className="bg-green-100 text-green-800">
              {processingResult.emails_extracted || 0} camp emails found
            </Badge>
          </div>
          <p className="text-sm text-green-800">
            {processingResult.message}
          </p>
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-3">
        <Button
          onClick={() => onComplete?.()}
          className="flex-1"
        >
          Continue Setup
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );

  const renderError = () => (
    <div className="text-center space-y-6">
      <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
        <XCircle className="w-8 h-8 text-red-600" />
      </div>
      
      <div>
        <h3 className="text-xl font-semibold text-gray-900 mb-2">
          Something Went Wrong
        </h3>
        <p className="text-gray-600">
          {error || 'We encountered an error while processing your emails.'}
        </p>
      </div>

      <div className="bg-red-50 rounded-lg p-4">
        <div className="flex items-center justify-center space-x-2">
          <AlertTriangle className="w-4 h-4 text-red-600" />
          <span className="text-sm text-red-800">
            Don't worry, you can try again or skip this step
          </span>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <Button
          onClick={handleRetryProcessing}
          disabled={isRetrying}
          className="flex-1"
        >
          {isRetrying ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <RefreshCw className="w-4 h-4 mr-2" />
          )}
          Try Again
        </Button>
        <Button
          variant="outline"
          onClick={handleSkip}
          className="flex-1"
        >
          Skip for Now
        </Button>
      </div>
    </div>
  );

  return (
    <Card className="card-shadow border-0">
      <CardHeader>
        <CardTitle className="flex items-center space-x-2">
          <Mail className="w-5 h-5 text-blue-600" />
          <span>Email Connection</span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {step === 'intro' && renderIntro()}
        {step === 'connecting' && renderConnecting()}
        {step === 'processing' && renderProcessing()}
        {step === 'success' && renderSuccess()}
        {step === 'error' && renderError()}
      </CardContent>
    </Card>
  );
}
